import React, { useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { ProductSheet } from "@/components/sales/ProductSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { formatKr } from "@/lib/format";
import { toast } from "sonner";
import { Package, Plus, Search, Pencil, Trash2 } from "lucide-react";

export default function ProductsPage() {
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [sheet, setSheet] = useState({ open: false, product: null });
  const [del, setDel] = useState(null);

  const load = () => api.get("/sales/products", { params: { include_inactive: true } }).then((r) => setItems(r.data.items)).catch((e) => setError(errorMessage(e)));
  useEffect(() => { load(); api.get("/sales/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    return s ? items.filter((p) => [p.name, p.description, p.sku].some((v) => (v || "").toLowerCase().includes(s))) : items;
  }, [items, q]);

  const remove = async () => {
    try {
      await api.delete(`/sales/products/${del.id}`);
      toast.success("Produkt slettet");
      setDel(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Produkter og tjenester" description="Faste varer og tjenester med pris og MVA-sats, klare til å legges på fakturaen." actions={<Button onClick={() => setSheet({ open: true, product: null })} data-testid="product-create-button"><Plus className="h-4 w-4 mr-2" /> Nytt produkt</Button>} />

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk etter navn eller varenr" className="pl-9" data-testid="product-search-input" aria-label="Søk i produkter" />
      </div>

      {error ? <EmptyState title="Kunne ikke laste produkter" description={error} actionLabel="Prøv igjen" onAction={load} /> : !items ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Package} title={q ? "Ingen treff" : "Ingen produkter ennå"} description={q ? "Prøv et annet søk." : "Legg inn timepriser, varer eller faste tjenester. Du kan alltid skrive fritekstlinjer på fakturaen også."} actionLabel={q ? undefined : "Legg til første produkt"} onAction={() => setSheet({ open: true, product: null })} testId="products-empty-state" />
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <Table data-testid="product-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Navn</TableHead>
                <TableHead className="hidden sm:table-cell w-[110px]">Enhet</TableHead>
                <TableHead className="text-right w-[130px]">Pris eks. mva</TableHead>
                <TableHead className="text-right w-[90px]">MVA</TableHead>
                <TableHead className="w-[100px] hidden sm:table-cell">Status</TableHead>
                <TableHead className="w-[90px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  <TableCell><p className="font-medium">{p.name}</p>{p.description ? <p className="text-xs text-muted-foreground line-clamp-1">{p.description}</p> : null}{p.sku ? <p className="text-[11px] text-muted-foreground">Varenr {p.sku}</p> : null}</TableCell>
                  <TableCell className="hidden sm:table-cell text-muted-foreground">{p.unit}</TableCell>
                  <TableCell className="text-right tnum font-medium whitespace-nowrap">{formatKr(p.unit_price)}</TableCell>
                  <TableCell className="text-right tnum">{p.vat_rate} %</TableCell>
                  <TableCell className="hidden sm:table-cell">{p.active === false ? <Badge variant="outline" className="text-muted-foreground">Inaktiv</Badge> : <Badge variant="outline" className="status-betalt border">Aktiv</Badge>}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-0.5">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSheet({ open: true, product: p })} aria-label="Rediger" data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDel(p)} aria-label="Slett" data-testid={`product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ProductSheet open={sheet.open} onOpenChange={(o) => setSheet((s) => ({ ...s, open: o }))} product={sheet.product} onSaved={load} defaults={meta?.invoice_settings} />

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader><AlertDialogTitle>Slette {del?.name}?</AlertDialogTitle><AlertDialogDescription>Eksisterende fakturalinjer påvirkes ikke.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="product-delete-cancel">Avbryt</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="product-delete-confirm">Slett</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
